/**
 * EnterpriseAppPermissionsCard — Settings card that checks ALICE's
 * enterprise application in the tenant and restores its Microsoft Graph
 * permissions through admin consent.
 *
 * Why it exists: if a customer deletes the ALICE enterprise application,
 * the next sign-in re-creates it through user consent with only the
 * delegated sign-in scope. The tenant is still active, so onboarding never
 * runs again and every sync fails. Admin consent puts the application
 * permissions back without re-onboarding or touching tenant data.
 *
 * Backend contract:
 *   GET /api/tenant/settings/enterprise-app
 *
 * Consent round trip: Microsoft only returns to a registered redirect URI,
 * so this reuses onboarding's (/onboarding). TenantOnboardingPage spots the
 * state minted here and sends the admin back to
 * /settings?section=integrations&permissions_consent=granted|error.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const PERMISSION_RESTORE_PURPOSE = "restore_enterprise_app";

// Entra can take a minute or two to apply a fresh admin consent to the
// tokens it issues, so keep checking for about two minutes before giving up.
const VERIFY_INTERVAL_MS = 10000;
const VERIFY_ATTEMPTS = 12;

const STATE_DISPLAY = {
  healthy: { title: "Permissions granted", Icon: CheckCircle2, className: "text-emerald-400" },
  partial: { title: "Some permissions are missing", Icon: AlertTriangle, className: "text-amber-400" },
  permissions_missing: { title: "No application permissions", Icon: XCircle, className: "text-red-400" },
  app_missing: { title: "Enterprise application not found", Icon: XCircle, className: "text-red-400" },
  check_failed: { title: "Could not verify permissions", Icon: AlertTriangle, className: "text-amber-400" },
  server_config: { title: "Server configuration problem", Icon: AlertTriangle, className: "text-amber-400" },
  not_applicable: { title: "Not used for this tenant", Icon: Info, className: "text-white/70" },
  over_privileged: { title: "More permissions than this plan allows", Icon: AlertTriangle, className: "text-amber-400" },
};

// Reads the state Microsoft echoes back from /adminconsent. Returns the
// decoded object only when it was minted by this card.
export function parseRestoreConsentState(state) {
  if (!state) return null;
  try {
    const decoded = JSON.parse(atob(state));
    return decoded?.purpose === PERMISSION_RESTORE_PURPOSE ? decoded : null;
  } catch {
    return null;
  }
}

function buildConsentUrl(tenantId, clientId) {
  const state = btoa(JSON.stringify({
    purpose: PERMISSION_RESTORE_PURPOSE,
    tenantId,
    timestamp: Date.now(),
  }));
  // Tenant-specific authority, not /common, so the consent lands in the
  // tenant ALICE is checking even when the admin is a guest elsewhere.
  const url = new URL(
    `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}/adminconsent`
  );
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", `${window.location.origin}/onboarding`);
  url.searchParams.set("state", state);
  return url.toString();
}

function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return detail?.message || error?.message || fallback;
}

export default function EnterpriseAppPermissionsCard({
  apiBase,
  authHeaders,
  consentResult,
  consentError,
  onConsentHandled,
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [notice, setNotice] = useState(null); // { tone, text }

  // SettingsPage re-creates getAuthHeaders on every render; read the latest
  // through a ref so it can't retrigger the mount effect.
  const authHeadersRef = useRef(authHeaders);
  authHeadersRef.current = authHeaders;
  const mountedRef = useRef(true);
  const startedRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    const headers = await authHeadersRef.current();
    const r = await axios.get(`${apiBase}/tenant/settings/enterprise-app`, { headers });
    return r.data;
  }, [apiBase]);

  const recheck = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStatus();
      if (mountedRef.current) setStatus(data);
    } catch (e) {
      if (mountedRef.current) setError(errorText(e, "Could not check the enterprise application"));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fetchStatus]);

  const verifyAfterConsent = useCallback(async () => {
    setVerifying(true);
    setNotice({
      tone: "info",
      text: "Admin consent granted. Waiting for Microsoft Entra to apply the permissions - this usually takes under a minute.",
    });
    for (let attempt = 1; attempt <= VERIFY_ATTEMPTS; attempt++) {
      try {
        const data = await fetchStatus();
        if (!mountedRef.current) return;
        setStatus(data);
        setError(null);
        setLoading(false);
        if (data.healthy) {
          setVerifying(false);
          setNotice({ tone: "success", text: "Permissions restored. ALICE can sync this tenant again." });
          toast.success("Enterprise app permissions restored");
          return;
        }
      } catch (e) {
        if (!mountedRef.current) return;
        setError(errorText(e, "Could not check the enterprise application"));
        setLoading(false);
      }
      if (attempt < VERIFY_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, VERIFY_INTERVAL_MS));
        if (!mountedRef.current) return;
      }
    }
    setVerifying(false);
    setNotice({
      tone: "warning",
      text: "Consent was recorded but the permissions haven't appeared yet. Entra can take a few minutes - use Re-check shortly. If they still don't appear, make sure the account that approved was a Global Administrator or Privileged Role Administrator.",
    });
  }, [fetchStatus]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!consentResult) {
      recheck();
      return;
    }
    // Clear the consent flags from the URL so a refresh doesn't replay them.
    onConsentHandled?.();
    if (consentResult === "granted") {
      verifyAfterConsent();
    } else {
      setNotice({
        tone: "error",
        text: consentError
          ? `Admin consent was not completed: ${consentError}`
          : "Admin consent was not completed.",
      });
      recheck();
    }
    // Runs once on mount: the consent result is only meaningful on arrival.
  }, []);

  // Only the backend's client ID: a tenant on a reduced plan consented to a
  // different ALICE app registration than the sign-in one this build knows,
  // and restoring against the sign-in app would grant the full permission set.
  const clientId = status?.client_id;
  const tenantId = status?.tenant_id;
  const canRestore = Boolean(status?.can_repair && tenantId && clientId);
  const display = STATE_DISPLAY[status?.state] || STATE_DISPLAY.check_failed;
  const needsRestore = status && !status.healthy && status.can_repair;

  const restore = () => {
    if (!canRestore) return;
    window.location.href = buildConsentUrl(tenantId, clientId);
  };

  return (
    <Card data-testid="enterprise-app-permissions-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <ShieldCheck className="h-5 w-5" />
          Enterprise App Permissions
        </CardTitle>
        <CardDescription>
          Checks the Microsoft Graph permissions held by ALICE&apos;s enterprise
          application in your Microsoft Entra tenant, and restores them if
          they&apos;ve been removed - for example after the enterprise
          application was deleted.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading && !status ? (
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking permissions…
          </div>
        ) : status ? (
          <>
            <div className="flex items-start gap-3" data-testid="enterprise-app-status">
              <display.Icon className={`h-5 w-5 mt-0.5 shrink-0 ${display.className}`} />
              <div className="space-y-1">
                <p className={`font-medium ${display.className}`}>{display.title}</p>
                <p className="text-sm text-white/80">{status.message}</p>
                {status.error_detail && (
                  <p className="text-xs text-white/50 break-words">{status.error_detail}</p>
                )}
                {status.service_tier && status.service_tier !== "full" && (
                  <p className="text-xs text-white/60" data-testid="enterprise-app-plan">
                    Plan: {status.service_tier_label}
                    {status.client_id ? ` · Application ID ${status.client_id}` : ""}
                  </p>
                )}
              </div>
            </div>

            {status.permissions_known && status.state !== "not_applicable" && (
              <div className="border rounded-lg divide-y divide-white/10">
                {status.permissions.map((perm) => (
                  <div
                    key={perm.key}
                    className="flex flex-wrap items-center justify-between gap-2 p-3"
                    data-testid={`enterprise-app-permission-${perm.key}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm">
                        {perm.label}
                        {!perm.required && (
                          <span className="ml-2 text-xs text-white/50">(optional)</span>
                        )}
                      </p>
                      <p className="text-xs text-white/60">{perm.used_for}</p>
                    </div>
                    {perm.granted_as ? (
                      <Badge variant="secondary" className="font-mono text-xs">
                        {perm.granted_as}
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className={perm.required ? "text-red-400 border-red-400/50" : "text-white/60"}
                        title={perm.accepted_roles.join(" or ")}
                      >
                        Missing
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(status.excess_roles?.length > 0 || status.sign_in_app_roles?.length > 0) && (
              <div
                className="border border-amber-400/40 rounded-lg p-3 space-y-3"
                data-testid="enterprise-app-excess"
              >
                <p className="text-sm font-medium text-amber-300">Permissions to remove</p>
                {status.excess_roles?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-white/70">
                      ALICE&apos;s enterprise application{status.client_id ? ` (application ID ${status.client_id})` : ""}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {status.excess_roles.map((role) => (
                        <Badge key={role} variant="outline" className="font-mono text-xs text-amber-300 border-amber-400/50">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {status.sign_in_app_roles?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-white/70">
                      ALICE&apos;s sign-in enterprise application{process.env.REACT_APP_AZURE_CLIENT_ID ? ` (application ID ${process.env.REACT_APP_AZURE_CLIENT_ID})` : ""}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {status.sign_in_app_roles.map((role) => (
                        <Badge key={role} variant="outline" className="font-mono text-xs text-amber-300 border-amber-400/50">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-xs text-white/60">
                  Remove these in the Microsoft Entra admin center under Enterprise
                  applications, then use Re-check. Admin consent can&apos;t take
                  permissions away.
                </p>
              </div>
            )}
          </>
        ) : null}

        {notice && (
          <Alert
            variant={notice.tone === "error" ? "destructive" : "default"}
            data-testid="enterprise-app-consent-notice"
          >
            {notice.tone === "success" ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : notice.tone === "info" ? (
              <Loader2 className={`h-4 w-4 ${verifying ? "animate-spin" : ""}`} />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            <AlertDescription>{notice.text}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTitle>Check failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {status?.can_repair && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Before you restore</AlertTitle>
            <AlertDescription className="space-y-1 text-sm">
              <p>
                You&apos;ll be sent to Microsoft to approve ALICE&apos;s
                permissions for this tenant, then brought back here.
              </p>
              <p>
                Approve as a <strong>Global Administrator</strong> or{" "}
                <strong>Privileged Role Administrator</strong> - other roles
                can&apos;t grant Microsoft Graph application permissions.
              </p>
              <p>Nothing in ALICE is reset: apps, packages and deployments are untouched.</p>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            onClick={recheck}
            disabled={loading || verifying}
            data-testid="enterprise-app-recheck"
          >
            {loading || verifying ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Re-check
          </Button>
          {status?.can_repair && (
            <Button
              variant={needsRestore ? "default" : "outline"}
              onClick={restore}
              disabled={!canRestore || verifying}
              data-testid="enterprise-app-restore"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              {needsRestore ? "Restore permissions" : "Re-grant admin consent"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
